import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Send, Plus, Trash2, Upload, CheckCircle2, FileText } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { useActiveTenant } from '../contexts/tenant'
import { usePermissions } from '../contexts/permissions'
import type { Product } from '@wsfull/shared'

type Line = { sku: string; name: string; quantity: string }

function readAsDataURL(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader()
    r.onload = () => res(r.result as string)
    r.onerror = rej
    r.readAsDataURL(file)
  })
}

export function EnviosClientePage() {
  const tenantId = useActiveTenant()
  const { can } = usePermissions()
  const canEdit = can('meus-envios', 'edit')
  const [customerName, setCustomerName] = useState('')
  const [nfNumber, setNfNumber] = useState('')
  const [nfSeries, setNfSeries] = useState('')
  const [nfKey, setNfKey] = useState('')
  const [labelName, setLabelName] = useState<string | null>(null)
  const [labelBase64, setLabelBase64] = useState<string | null>(null)
  const [nfName, setNfName] = useState<string | null>(null)
  const [nfBase64, setNfBase64] = useState<string | null>(null)
  const [lines, setLines] = useState<Line[]>([])
  const [pick, setPick] = useState('')
  const [qty, setQty] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null)

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ['products', tenantId],
    queryFn: async () => (await api.get('/products', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  function addLine() {
    const p = products.find(x => x.id === pick)
    const n = Number(qty) || 0
    if (!p || n <= 0) return
    setLines(l => [...l.filter(x => x.sku !== p.sku), { sku: p.sku, name: p.name, quantity: String(n) }])
    setPick(''); setQty('')
  }

  async function onLabel(file: File) {
    setLabelName(file.name)
    const reader = new FileReader()
    reader.onload = () => setLabelBase64(String(reader.result))
    reader.readAsDataURL(file)
  }

  async function submit() {
    if (!lines.length || busy) return
    setBusy(true); setResult(null)
    try {
      const { data } = await api.post('/outbound/client-shipment', {
        tenantId, customerName, nfNumber, nfSeries, nfKey,
        labelBase64: labelBase64 ?? undefined,
        invoiceBase64: nfBase64 ?? undefined,
        items: lines.map(l => ({ sku: l.sku, quantity: Number(l.quantity) || 0 })),
      })
      const nf = data.notFound?.length ? ` (SKUs ignorados: ${data.notFound.join(', ')})` : ''
      setResult({ ok: true, text: `Pedido ${data.orderNumber} criado — ${data.items} item(ns). Vá em Pedidos/Expedir para despachar (aí baixa o estoque).${nf}` })
      setCustomerName(''); setNfNumber(''); setNfSeries(''); setNfKey(''); setLines([])
      setLabelName(null); setLabelBase64(null); setNfName(null); setNfBase64(null)
    } catch (e: any) {
      setResult({ ok: false, text: e?.response?.data?.title ?? 'Falha ao registrar envio' })
    } finally { setBusy(false) }
  }

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Meus envios</h2>
        <p className="text-sm text-gray-500">Cria um pedido de envio (destinatário + NF + etiqueta). Entra como <b>pedido a expedir</b> — o estoque baixa quando você despacha em Pedidos/Expedir.</p>
      </div>

      {!canEdit && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Seu perfil tem <b>apenas leitura</b> em Meus envios — não é possível criar pedidos. Fale com o administrador se precisar deste acesso.
        </div>
      )}

      <Card>
        <CardContent className="space-y-4 py-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Destinatário" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Nome do cliente" />
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Input label="Nº da NF" value={nfNumber} onChange={e => setNfNumber(e.target.value)} placeholder="ex.: 83848" />
              </div>
              <Input label="Série" value={nfSeries} onChange={e => setNfSeries(e.target.value)} placeholder="ex.: 1" />
            </div>
          </div>
          <Input label="Chave da NF (opcional, 44 dígitos)" value={nfKey} onChange={e => setNfKey(e.target.value.replace(/\D/g, ''))} placeholder="para permitir bipar depois" />

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Etiqueta de envio (PDF/imagem)</label>
              <label className="inline-flex w-full cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                <Upload className="h-4 w-4 shrink-0" /> <span className="truncate">{labelName ?? 'Escolher arquivo'}</span>
                <input type="file" accept=".pdf,image/*" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) onLabel(f); e.target.value = '' }} />
              </label>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Nota fiscal / DANFE (PDF)</label>
              <label className="inline-flex w-full cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                <FileText className="h-4 w-4 shrink-0" /> <span className="truncate">{nfName ?? 'Escolher arquivo'}</span>
                <input type="file" accept=".pdf,application/pdf" className="hidden"
                  onChange={async e => { const f = e.target.files?.[0]; if (f) { setNfName(f.name); setNfBase64(await readAsDataURL(f)) } e.target.value = '' }} />
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 py-5">
          <p className="text-sm font-medium text-gray-700">Itens do envio</p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[200px] flex-1">
              <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Produto</label>
              <select value={pick} onChange={e => setPick(e.target.value)}
                className="h-10 w-full rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none">
                <option value="">Selecione…</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
              </select>
            </div>
            <div className="w-24">
              <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Qtd</label>
              <input inputMode="numeric" value={qty} onChange={e => setQty(e.target.value.replace(/[^\d]/g, ''))}
                className="h-10 w-full rounded-lg border border-gray-300 px-3 text-center text-sm focus:border-blue-500 focus:outline-none" />
            </div>
            <Button variant="secondary" onClick={addLine} disabled={!canEdit || !pick || !(Number(qty) > 0)}>
              <Plus className="h-4 w-4" /> Adicionar
            </Button>
          </div>

          {lines.length > 0 && (
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
              {lines.map(l => (
                <li key={l.sku} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className="font-mono font-medium text-gray-900">{l.sku}</span>
                  <span className="flex-1 text-gray-600">{l.name}</span>
                  <span className="font-semibold text-gray-900">{l.quantity} un</span>
                  <button className="text-gray-400 hover:text-red-600" onClick={() => setLines(a => a.filter(x => x.sku !== l.sku))}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {result && (
        <div className={`flex items-center gap-2 rounded-lg border px-4 py-3 text-sm ${result.ok ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-700'}`}>
          {result.ok && <CheckCircle2 className="h-5 w-5 shrink-0" />}
          {result.text}
        </div>
      )}

      <div className="flex justify-end">
        <Button size="lg" loading={busy} disabled={!canEdit || lines.length === 0} onClick={submit}>
          <Send className="h-5 w-5" /> Criar pedido de envio
        </Button>
      </div>
    </div>
  )
}
